import { useTranslation } from 'react-i18next';

import {
  WordSelectionMode,
  WordSelectionModeDetails,
} from '../../../../constants/MemoryGames/wordSelectionMode';
import { SVGIconType } from '../../../../constants/svgIcon';
import { TtsLocale } from '../../../../models/Base';
import {
  getQuickCreateGameModeProps,
  QuickCreateGameMode,
} from '../../../../models/DraftStore';
import {
  TextOrderingUnit,
  TextOrderingUnitType,
} from '../../../../models/GameStore';
import { WebBoosterType } from '../../../../models/UserStore';
import {
  AppBar,
  BubbleStepsProgressBar,
  PngIcon,
  SvgIcon,
  TextJoyGroup,
} from '../../../Molecules';

import styles from './CreateMemoryGameSelectWordsTemplate.module.css';

type selectWordCellProps = {
  mode: WordSelectionMode;
  units: TextOrderingUnit[];
};

type CreateMemoryGameSelectWordsTemplateProps = {
  type: QuickCreateGameMode | undefined;
  webBoosterType: WebBoosterType | undefined;
  locales: TtsLocale[];
  selectWordCells: selectWordCellProps[];
  showRequiredWarning: boolean;
  expectedPayment: number;
  onClickHelpVideo: () => void;
  onClickMode: (cellIndex: number) => void;
  onUnitClick: (cellIndex: number, unitIndex: number) => void;
  onClickBack: () => void;
  onClickNext: () => void;
  onClickWorldSelectGuide: () => void;
};

export const CreateMemoryGameSelectWordsTemplate = (
  props: CreateMemoryGameSelectWordsTemplateProps,
) => {
  const {
    type,
    webBoosterType,
    locales,
    selectWordCells,
    showRequiredWarning,
    expectedPayment,
    onClickHelpVideo,
    onClickMode,
    onUnitClick,
    onClickBack,
    onClickNext,
    onClickWorldSelectGuide,
  } = props;
  const { t } = useTranslation();

  const splitWordCells = (
    wordCell: selectWordCellProps,
  ): (TextOrderingUnit[] | null)[] => {
    const result: (TextOrderingUnit[] | null)[] = [];
    let currentGroup: TextOrderingUnit[] = [];

    wordCell.units.forEach((unit) => {
      if (unit.type === TextOrderingUnitType.Hint && unit.key === '\n') {
        if (currentGroup.length > 0) {
          result.push(currentGroup);
          currentGroup = [];
        }
        result.push(null); // Push null to represent the newline
      } else {
        currentGroup.push(unit);
      }
    });

    if (currentGroup.length > 0) {
      result.push(currentGroup);
    }

    return result;
  };

  return (
    <div className={styles.createMemorySelectWords}>
      <AppBar
        title={
          type ? t(getQuickCreateGameModeProps(type, webBoosterType).name) : ''
        }
        onClickBack={onClickBack}
        trailing={
          <>
            {expectedPayment !== 0 && (
              <div className={styles.paymentContainer}>
                <PngIcon name={'CreationStamina'} width={24} height={24} />
                <div className={styles.payTextContainer}>
                  {t('Pay: ')}
                  <p
                    style={{
                      whiteSpace: 'nowrap',
                      fontWeight: 'bold',
                      fontSize: '14px',
                      marginLeft: '3px',
                      lineHeight: 'normal',
                    }}
                  >
                    {expectedPayment}
                  </p>
                </div>
                {/* {`${t('Pay: ')}${expectedPayment}`} */}
              </div>
            )}
          </>
        }
      />
      <div className={styles.userGuideBanner} onClick={onClickHelpVideo}>
        <SvgIcon
          className={styles.questionIcon}
          name={SVGIconType.QuestionHelp}
          color={'#333333'}
        />
        <div className={styles.bannerText}>{t('User Guide')}</div>
        <div className={styles.playerContainer}>
          <SvgIcon
            className={styles.playerIcon}
            name={SVGIconType.PlayerPlay}
            color={'white'}
          />
        </div>
      </div>
      <div className={styles.container}>
        <div className={styles.titleContainer}>
          {/* <p className={styles.gameTypeName}>
            {t(getQuickCreateGameModeProps(type).name)}
          </p> */}
          <p className={styles.gameTypeName}>{t('Memory Practice')}</p>
        </div>
        <div className={styles.progress}>
          <BubbleStepsProgressBar currentStep={2} totalSteps={3} />
        </div>

        {/* <p className={styles.title}>{t('Select Words')}</p> */}

        {selectWordCells.map((wordCell, index) => {
          const allWordsHint = wordCell.units.every(
            (unit) => unit.type === TextOrderingUnitType.Hint,
          );
          const splitCells = splitWordCells(wordCell);

          return (
            <div
              key={index}
              className={styles.gameCard}
              style={{
                border:
                  showRequiredWarning && allWordsHint
                    ? '1px solid #D95141'
                    : undefined,
              }}
            >
              <div className={styles.gameFooter}>
                <div className={styles.gameNumber}>{index + 1}</div>
                <WordSelectionModeRow
                  mode={wordCell.mode}
                  onClick={() => {
                    onClickMode(index);
                  }}
                />
              </div>
              <div className={styles.gameHeader}>
                {wordCell.mode === WordSelectionMode.AIPickWords ? (
                  <div
                    className={styles.wordSelectGuide}
                    style={{
                      color:
                        showRequiredWarning && allWordsHint
                          ? '#D95141'
                          : '#F08F00',
                      cursor: 'default',
                    }}
                  >
                    {t('Each practice will offer different keywords')}
                  </div>
                ) : (
                  <div
                    onClick={onClickWorldSelectGuide}
                    className={styles.wordSelectGuide}
                    style={{
                      color:
                        showRequiredWarning && allWordsHint
                          ? '#D95141'
                          : '#F08F00',
                    }}
                  >
                    {t('Click the word to select or deselect')}
                    <SvgIcon
                      name={SVGIconType.GameInstruction}
                      color={
                        showRequiredWarning && allWordsHint
                          ? '#D95141'
                          : '#F08F00'
                      }
                      width={20}
                      height={20}
                    />
                  </div>
                )}

                <div
                  className={`${styles.cardContent} ${
                    wordCell.mode === WordSelectionMode.AIPickWords
                      ? styles.cardContentPointer
                      : ''
                  }`}
                  onClick={() => {
                    if (wordCell.mode === WordSelectionMode.AIPickWords) {
                      onClickMode(index);
                    }
                  }}
                >
                  {splitCells.map((group, groupIndex) => {
                    if (group === null) {
                      const prevGroup = splitCells[groupIndex - 1];
                      const nextGroup = splitCells[groupIndex + 1];

                      if (prevGroup === null || nextGroup === null) {
                        return (
                          // new paragraph
                          <div
                            key={groupIndex}
                            style={{
                              padding: '0.25rem',
                            }}
                          />
                        );
                      }
                    }

                    if (group === null) {
                      // new line
                      return null;
                    }

                    const startIndex = splitCells
                      .slice(0, groupIndex)
                      .reduce((sum, g) => sum + (g === null ? 1 : g.length), 0);

                    return (
                      <TextJoyGroup
                        type={type}
                        key={groupIndex}
                        onUnitClick={
                          wordCell.mode === WordSelectionMode.AIPickWords
                            ? undefined
                            : (unitIndex) => {
                                onUnitClick(index, startIndex + unitIndex);
                              }
                        }
                        highlightColor={
                          type === QuickCreateGameMode.Spelling
                            ? '#4C6CAE33'
                            : '#A8B8D8'
                        }
                        units={group}
                        locale={locales[index] ?? TtsLocale.US}
                      />
                    );
                  })}
                </div>
              </div>
            </div>
          );
        })}
      </div>
      <div className={styles.bottomBar}>
        <div className={styles.nextContainer}>
          <button className={styles.nextButton} onClick={onClickBack}>
            <SvgIcon
              className={styles.leftArrowIcon}
              name={SVGIconType.Arrow}
            />
            <span className={styles.nextText}>{t('Back')}</span>
          </button>
          <button className={styles.nextButton} onClick={onClickNext}>
            <span className={styles.nextText}>{t('Next')}</span>
            <SvgIcon
              className={styles.rightArrowIcon}
              name={SVGIconType.Arrow}
            />
          </button>
        </div>
      </div>
    </div>
  );
};

interface WordSelectionModeProps {
  mode: WordSelectionMode;
  onClick: () => void;
}

const WordSelectionModeRow: React.FC<WordSelectionModeProps> = ({
  mode,
  onClick,
}: WordSelectionModeProps) => {
  const { t } = useTranslation();
  return (
    <div className={styles.wordSelectionMode} onClick={onClick}>
      <SvgIcon
        name={WordSelectionModeDetails[mode].icon}
        width={24}
        height={24}
        color="#ffffff"
      />
      <p className={styles.wordSelectionModeText}>
        {t(WordSelectionModeDetails[mode].displayName)}
      </p>
      <SvgIcon
        name={SVGIconType.Triangle}
        width={20}
        height={20}
        color={'#ffffff'}
      />
    </div>
  );
};
